// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A filter menu component for asset assignment status selection.
 * This module provides a controlled interface for selecting
 * multiple asset assignment statuses from a predefined list.
 * It integrates with the standard filter menu component and
 * supports customizable labels.
 *
 * Key features:
 * - Status selection
 * - Multiple selection
 * - Label customization
 * - State management
 * - Filter integration
 */
import { FilterMenu } from '@/components/filter-menu';
import {
  ASSET_ASSIGNMENT_STATUSES,
  ASSET_ASSIGNMENT_STATUS_LABELS,
  AssetAssignmentStatus,
} from '@/constants/asset-assignment-status';

const DEFAULT_LABEL = 'Assignment';

/**
 * Props for configuring the AssetAssignmentFilterMenu component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Currently selected assignment statuses.
   * Empty array means no statuses are selected.
   */
  value: AssetAssignmentStatus[];

  /**
   * Custom label for the filter menu button.
   * Displayed when menu is closed.
   *
   * @default "Assignment"
   */
  label?: string;

  /**
   * Callback fired when selection changes.
   * Receives updated array of selected statuses.
   *
   * @param value - New array of selected statuses
   */
  onChange: (value: AssetAssignmentStatus[]) => void;
}

/**
 * A filter menu component for asset assignment status selection.
 * Provides a controlled interface for selecting multiple asset
 * assignment statuses from a predefined list. Integrates with
 * the standard filter menu component for consistent UX.
 *
 * Features:
 * - Status selection
 * - Multiple selection
 * - Label customization
 * - State management
 * - Filter integration
 * - Consistent UX
 *
 * Usage example:
 * ```tsx
 * function AssetList() {
 *   const [statuses, setStatuses] = useState<AssetAssignmentStatus[]>([]);
 *
 *   return (
 *     <AssetAssignmentFilterMenu
 *       value={statuses}
 *       onChange={setStatuses}
 *       label="Filter by assignment"
 *     />
 *   );
 * }
 * ```
 */
export function AssetAssignmentFilterMenu(props: Props) {
  // Use default or custom label
  const label = props.label ?? DEFAULT_LABEL;

  // Map statuses to filter menu options
  const options = ASSET_ASSIGNMENT_STATUSES.map((status) => ({
    id: status,
    label: ASSET_ASSIGNMENT_STATUS_LABELS[status],
    isActive: props.value.includes(status),
  }));

  /**
   * Handles option selection changes.
   * Updates the selected statuses array based on selection.
   *
   * @param value - The status being toggled
   * @param isActive - Whether the status is being selected or deselected
   */
  const handleChangeOption = (
    value: AssetAssignmentStatus,
    isActive: boolean
  ) => {
    if (isActive) {
      return props.onChange([...props.value, value]);
    }

    props.onChange(props.value.filter((status) => status !== value));
  };

  return (
    <FilterMenu
      label={label}
      options={options}
      onChangeOption={handleChangeOption}
    />
  );
}
